import React, {useEffect} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import { keyframes } from '@emotion/react';
import agent from '../agent';
import {connect} from 'react-redux';
import {
  FOLLOW_USER,
  UNFOLLOW_USER,
  PROFILE_PAGE_LOADED,
  PROFILE_PAGE_UNLOADED,
  LOGOUT,
} from '../constants/actionTypes';
import TopBar from "./TopBar";
import useTranslation from "../customHooks/translations";
import FreakList from "./Freak/FreakList";
import GarageSection from "./FreakItems/GarageSection";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER} from "../constants/theme";
import {SOCIAL_PLATFORMS} from "../utils/socialLinks";
import EditIcon from "@mui/icons-material/Edit";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LogoutIcon from '@mui/icons-material/Logout';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedIcon from '@mui/icons-material/Verified';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth0 } from "@auth0/auth0-react";
import {getFreakIcon, getFreakName} from "../utils/freakUtils";
import AmbassadorGiftsComponent from './Ambassador/AmbassadorGiftsComponent';
import FreaksProfileSection from './TheFreaks/FreaksProfileCard';
import UserSafetyActions from './UserSafety/UserSafetyActions';

const ambassadorPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,200,50,0.5), 0 0 16px rgba(255,200,50,0.15); }
  50%       { box-shadow: 0 0 0 8px rgba(255,200,50,0), 0 0 24px rgba(255,200,50,0.3); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const statItem = (value, label, onClick) => (
  <Box
    sx={{ textAlign: 'center', cursor: onClick ? 'pointer' : 'default', flex: 1 }}
    onClick={onClick}
  >
    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1 }}>{value ?? 0}</Typography>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}>{label}</Typography>
  </Box>
);

const Profile = (props) => {
  const params = useParams();
  const translation = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const context = props.context || props?.currentUser?.context || 'wof';
  const username = params.username.replace('@', '');

  const loadProfile = () => props.onLoad(Promise.all([
    agent.Profile.get(username),
    agent.Freaks.feed(0, context, 'desktop', undefined, undefined, 100, 'none', 'none', 'none', 'none', 'none', 'none', username)
  ]));

  useEffect(() => {
    loadProfile();
    props.onUnload();
  }, []);

  const profile = props.profile;
  if (!profile) return null;

  const isUser = !!props.currentUser?.username
    && props.currentUser.username.toLowerCase() === (profile.username || username).toLowerCase();

  const feedCards = [
    profile.youtubeFeedsCount && {
      icon: <YouTubeIcon sx={{ color: '#FF0000', width: 36, height: 36 }} />,
      label: 'YouTube',
      count: profile.youtubeFeedsCount,
      onClick: () => navigate(`/user-feeds/@${profile.username}/youtube`),
    },
    profile.instagramFeedsCount && {
      icon: <InstagramIcon sx={{ color: '#C13584', width: 36, height: 36 }} />,
      label: 'Instagram',
      count: profile.instagramFeedsCount,
      onClick: () => navigate(`/user-feeds/@${profile.username}/instagram`),
    },
    profile.facebookFeedsCount && {
      icon: <FacebookIcon sx={{ color: '#1877F2', width: 36, height: 36 }} />,
      label: 'Facebook',
      count: profile.facebookFeedsCount,
      onClick: () => navigate(`/user-feeds/@${profile.username}/facebook`),
    },
  ].filter(Boolean);

  return (
    <>
      <TopBar/>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)', pb: 12 }}>
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>

          {/* Hero banner */}
          <Box
            sx={{
              position: 'relative',
              height: 160,
              background: props?.profile?.backgroundImageUrl
                ? `url("${props.profile.backgroundImageUrl}") center/cover`
                : 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
              mb: 7,
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
            <Avatar
              alt={profile.username}
              src={profile?.imageUrl}
              sx={{
                width: 90,
                height: 90,
                border: profile.isAmbassador ? '3px solid gold' : '3px solid #0d1b2a',
                position: 'absolute',
                bottom: -45,
                left: 16,
                borderRadius: 3,
                boxShadow: profile.isAmbassador
                  ? '0 4px 20px rgba(0,0,0,0.6), 0 0 20px rgba(255,200,50,0.5)'
                  : '0 4px 20px rgba(0,0,0,0.6)',
                animation: profile.isAmbassador ? `${ambassadorPulse} 2.5s ease-in-out infinite` : 'none',
              }}
            />
          </Box>

          {/* Username + bio */}
          <Box sx={{ px: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                  {profile.username}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  @{profile.username}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getFreakIcon(profile?.context)}
              </Box>
            </Box>

            {profile.bio && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1, lineHeight: 1.55 }}>
                {profile.bio}
              </Typography>
            )}

            {SOCIAL_PLATFORMS.some(({ key }) => profile[key]) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {SOCIAL_PLATFORMS.filter(({ key }) => profile[key]).map(({ key, label, Icon, color }) => (
                  <IconButton
                    key={key}
                    component="a"
                    href={profile[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    size="small"
                    sx={{ color, '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' } }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </IconButton>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <VerifiedIcon sx={{ fontSize: 14, color: DEFAULT_THEME_COLOR }} />
              <Link
                to="https://www.freak-land.com"
                target="_blank"
                style={{ color: DEFAULT_THEME_COLOR, fontSize: '0.8rem', textDecoration: 'none' }}
              >
                {translation.profile.verified}
              </Link>
            </Box>

            {profile.isAmbassador && (
              <Box
                sx={{
                  mt: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  border: '1px solid rgba(255,200,50,0.35)',
                  background: 'linear-gradient(135deg, rgba(255,200,50,0.08) 0%, rgba(255,160,0,0.04) 100%)',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, height: 2,
                    background: 'linear-gradient(90deg, transparent, gold, transparent)',
                    backgroundSize: '200% auto',
                    animation: `${shimmer} 3s linear infinite`,
                  },
                }}
              >
                <WorkspacePremiumIcon sx={{ color: 'gold', fontSize: 28, flexShrink: 0 }} />
                <Box>
                  <Typography
                    sx={{
                      color: 'gold',
                      fontWeight: 800,
                      fontSize: '0.8rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                    }}
                  >
                    {translation.profile.ambassador}
                  </Typography>
                  {profile.ambassadorContext && (
                    <Typography sx={{ color: 'rgba(255,200,50,0.7)', fontSize: '0.78rem', fontWeight: 500 }}>
                      {getFreakName(profile.ambassadorContext, translation) || profile.ambassadorContext}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Ambassador gifts — only visible to the profile owner */}
          {isUser && profile.isAmbassador && <AmbassadorGiftsComponent />}

          {isUser && props.currentUser?.userRoles?.some(r => r.role === 'admin') && (
            <Box sx={{ px: 2, mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  display: 'block',
                  mb: 1,
                }}
              >
                Admin
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AdminPanelSettingsIcon />}
                sx={{
                  color: DEFAULT_THEME_COLOR,
                  borderColor: DEFAULT_THEME_COLOR,
                  zIndex: 0,
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' },
                  '&:focus': { outline: 'none' },
                }}
                onClick={() => navigate('/admin/feedback')}
              >
                Feedback Dashboard
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AdminPanelSettingsIcon />}
                sx={{
                  color: DEFAULT_THEME_COLOR,
                  borderColor: DEFAULT_THEME_COLOR,
                  zIndex: 0,
                  borderRadius: 2,
                  ml: 1,
                  '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' },
                  '&:focus': { outline: 'none' },
                }}
                onClick={() => navigate('/admin/moderation')}
              >
                Moderation
              </Button>
            </Box>
          )}

          {/* Stats */}
          <Box
            sx={{
              mx: 2,
              mb: 2,
              background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-around',
            }}
          >
            {statItem(profile.freaksCount, translation.profile.freaks)}
            {statItem(profile.points, translation.profile.points)}
            {statItem(profile.followedByCount, translation.profile.followers, () => navigate(`/followed-by/@${username}`))}
            {statItem(profile.followingCount, translation.profile.following, () => navigate(`/following/@${username}`))}
          </Box>

          {/* The Freaks game card (level, season rank, record) */}
          <FreaksProfileSection username={profile.username || username} enabled={!!props.currentUser?.username}/>

          {/* Feed cards */}
          {feedCards.length > 0 && (
            <Box sx={{ px: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                {translation.profile.freakFeeds}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                {feedCards.map((feed, i) => (
                  <Box
                    key={i}
                    onClick={feed.onClick}
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 2.5,
                      px: 2,
                      py: 1.5,
                      minWidth: 160,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.4)' },
                    }}
                  >
                    {feed.icon}
                    <Box>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>{feed.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {feed.count} {feed.count > 1 ? 'feeds' : 'feed'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Hub chips */}
          {profile?.freakHubs?.length > 0 && (
            <Box sx={{ px: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                {translation.profile.freakHubs}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                {profile.freakHubs.map(hub => (
                  <Box
                    key={hub.slug}
                    onClick={() => navigate(`/freak-hub/${hub.slug}`)}
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 2.5,
                      px: 2,
                      py: 1.5,
                      minWidth: 160,
                      maxWidth: 200,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.4)' },
                    }}
                  >
                    <Avatar src={hub?.imageUrl} alt={hub.name} sx={{ width: 36, height: 36, borderRadius: 1.5 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {hub.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {hub.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ px: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {isUser && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon/>}
                  sx={{ color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR, zIndex: 0, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' }, '&:focus': { outline: 'none' } }}
                  onClick={() => navigate('/settings')}
                >
                  {translation.profile.editMessage}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EmojiEventsIcon sx={{ color: 'gold' }}/>}
                  sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', zIndex: 0, borderRadius: 2, '&:hover': { borderColor: 'gold', color: 'gold' }, '&:focus': { outline: 'none' } }}
                  onClick={() => navigate('/prices-history')}
                >
                  {translation.profile.prizeHistory}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<YouTubeIcon sx={{ color: '#FF0000' }}/>}
                  sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', zIndex: 0, borderRadius: 2, '&:hover': { borderColor: '#FF0000', color: '#FF0000' }, '&:focus': { outline: 'none' } }}
                  onClick={() => navigate('/top-feeds')}
                >
                  {translation.profile.topFeeds}
                </Button>
              </>
            )}

            {!isUser && !profile.blocked && (
              <Button
                variant="contained"
                size="small"
                sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, zIndex: 0, borderRadius: 2, px: 3, '&:hover': { background: DEFAULT_GRADIENT_HOVER }, '&:focus': { outline: 'none' } }}
                onClick={(e) => {
                  e.preventDefault();
                  if (profile.following) props.onUnfollow(profile.username);
                  else props.onFollow(profile.username);
                }}
              >
                {profile.following ? translation.profileFavorites.unfollow : translation.profileFavorites.follow} {profile.username}
              </Button>
            )}

            {isUser && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LogoutIcon/>}
                sx={{ color: '#f44336', borderColor: 'rgba(244,67,54,0.4)', zIndex: 0, borderRadius: 2, '&:hover': { borderColor: '#f44336', backgroundColor: 'rgba(244,67,54,0.08)' }, '&:focus': { outline: 'none' } }}
                onClick={() => { logout(); props.onClickLogout(); navigate('/login'); }}
              >
                {translation.settings.logout}
              </Button>
            )}
          </Box>

          {!isUser && <UserSafetyActions profile={profile} onBlockedChange={loadProfile} />}

          {profile.blocked ? (
            <Box sx={{ textAlign: 'center', px: 2, py: 4 }} data-testid="profile-blocked-notice">
              <Typography sx={{ color: 'white', fontWeight: 600 }}>
                {translation.userBlock.blocked.replace('{username}', profile.username)}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mt: 0.5 }}>
                {translation.userBlock.blockedInfo}
              </Typography>
            </Box>
          ) : <>
          {/* Garage section */}
          <GarageSection
            username={username}
            context={context}
            isOwnProfile={isUser}
            strings={translation}
          />

          {/* Freak list */}
          <Box sx={{ px: 1 }}>
            <FreakList
              tab={props.tab}
              profileUsername={username}
              pager={props.pager}
              freaks={props.freaks}
              loading={props.loading}
              freaksCount={props.freaksCount}
              currentPage={props.currentPage}
              context={context}
            />
          </Box>
          </>}

        </Box>
      </Box>
    </>
  );
};

const mapStateToProps = state => ({
  ...state.freakList,
  currentUser: state.common.currentUser,
  profile: state.profile,
  context: state.common.context,
  device: state.common.device,
});

const mapDispatchToProps = dispatch => ({
  onClickLogout: () => dispatch({type: LOGOUT}),
  onFollow: username => dispatch({ type: FOLLOW_USER, payload: agent.Profile.follow(username) }),
  onLoad: payload => dispatch({type: PROFILE_PAGE_LOADED, payload}),
  onUnload: () => dispatch({type: PROFILE_PAGE_UNLOADED}),
  onUnfollow: username => dispatch({ type: UNFOLLOW_USER, payload: agent.Profile.unfollow(username) }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
export {Profile, mapStateToProps};
